import type { Bundle } from '@medplum/fhirtypes';

/** Safety valve so a pathological history cannot loop forever. */
const MAX_HISTORY_PAGES = 20;

/**
 * Earliest `meta.lastUpdated` in a history page. Medplum returns history newest-first,
 * but taking the minimum keeps this correct regardless of page ordering — unlike the
 * resource's current `meta.lastUpdated`, which moves on every save.
 */
export function createdAtFromHistoryBundle(bundle: Bundle | undefined): string | undefined {
  let earliest: string | undefined;
  for (const entry of bundle?.entry ?? []) {
    const lastUpdated = entry?.resource?.meta?.lastUpdated?.trim();
    if (!lastUpdated) continue;
    if (!earliest || lastUpdated < earliest) earliest = lastUpdated;
  }
  return earliest;
}

/** Next history page URL, when the history spans more than one page. */
export function nextHistoryPageUrl(bundle: Bundle | undefined): string | undefined {
  return bundle?.link?.find((link) => link.relation === 'next')?.url?.trim() || undefined;
}

export interface HistoryReader {
  readHistory: (resourceType: 'Practitioner', id: string) => Promise<Bundle>;
  get: (url: string) => Promise<unknown>;
}

/**
 * Create time for a resource: the oldest version across every history page.
 * Returns undefined when the history cannot be read.
 */
export async function fetchCreatedAtFromHistory(
  medplum: HistoryReader,
  resourceType: 'Practitioner',
  id: string,
): Promise<string | undefined> {
  let bundle: Bundle | undefined = await medplum.readHistory(resourceType, id);
  let earliest = createdAtFromHistoryBundle(bundle);

  for (let page = 0; page < MAX_HISTORY_PAGES; page += 1) {
    const next = nextHistoryPageUrl(bundle);
    if (!next) break;
    bundle = (await medplum.get(next)) as Bundle | undefined;
    const pageEarliest = createdAtFromHistoryBundle(bundle);
    if (pageEarliest && (!earliest || pageEarliest < earliest)) earliest = pageEarliest;
  }

  return earliest;
}
